import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { defaultDataIdFromObject, InMemoryCache } from 'apollo-cache-inmemory';
import { NormalizedCacheObject } from 'apollo-cache-inmemory/lib/types';
import { AuthStore } from '../store/AuthStore';
import { createStringifyErrorMessagesLink } from './links/stringifyErrorMessages';
import { createRetryOnNetworkErrorLink } from './links/retryOnNetworkError';
import { createWaitForTokenRefreshLink } from './links/waitForTokenRefresh';
import { createCheckAuthenticationErrorLink } from './links/checkAuthenticationError';
import { createSetAccessTokenContextLink } from './links/setAccessTokenContext';

export interface CreateApolloCacheProps {
  restoreCache?: NormalizedCacheObject;
}
export function createApolloCache({
  restoreCache
}: CreateApolloCacheProps = {}): InMemoryCache {
  const cache = new InMemoryCache({
    dataIdFromObject(value) {
      return defaultDataIdFromObject(value);
    }
  });

  if (restoreCache) {
    cache.restore(restoreCache);
  }

  return cache;
}

export interface CreateSimpleBrowserClientProps {
  uri: string;
}
export function createSimpleBrowserClient({
  uri
}: CreateSimpleBrowserClientProps) {
  const stringifyErrorMessages = createStringifyErrorMessagesLink();

  const sendHttpRequest = createHttpLink({
    uri
  });

  return new ApolloClient({
    link: ApolloLink.from([stringifyErrorMessages, sendHttpRequest]),
    cache: createApolloCache()
  });
}

export interface CreateBrowserClientWithAuthProps {
  uri: string;
  authStore: AuthStore;
}
export function createBrowserClientWithAuth({
  uri,
  authStore
}: CreateBrowserClientWithAuthProps) {
  const stringifyErrorMessages = createStringifyErrorMessagesLink();
  const retryOnNetworkError = createRetryOnNetworkErrorLink();
  const waitForTokenRefresh = createWaitForTokenRefreshLink(authStore);
  const checkAuthenticationError = createCheckAuthenticationErrorLink(
    authStore
  );
  const setAccessTokenContext = createSetAccessTokenContextLink(authStore);
  const sendHttpRequest = createHttpLink({
    uri
  });

  return new ApolloClient({
    link: ApolloLink.from([
      stringifyErrorMessages,
      retryOnNetworkError,
      waitForTokenRefresh,
      checkAuthenticationError,
      setAccessTokenContext,
      sendHttpRequest
    ]),
    cache: createApolloCache()
  });
}
